import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input, Option, Select } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { DishSelection } from "@admin/components/molecules/SelectWithAdd/DishSelection";
import {
  AcceptenceCriteriaColors,
  addFoodProductionControl,
  FoodControlPointInstance,
  FoodManagementControls,
  FoodProductionControl,
  heatTreatmentTemperatures,
  InstanceStatus,
  parseRemarkAndCorrectiveActions,
  updateFoodProductionControl,
  UpsertModalType,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { UpsertProcessingProductModal } from "./UpsertProcessingProductModal";

interface HeatModalProps extends ModalProps {
  foodControlPointInstance: FoodControlPointInstance;
  type:
    | FoodManagementControls.heatingTreatment
    | FoodManagementControls.reheatingFood;
  initialData?: FoodProductionControl;
}

export function HeatModal({
  foodControlPointInstance,
  type,
  initialData,
  ...props
}: HeatModalProps) {
  const { t } = useTranslation();
  const [selectedHeat, setSelectedHeat] = useState<Option>();
  const [isRemarkRequired, setIsRemarkRequired] = useState(false);
  const [correctiveActions, setCorrectiveActions] = useState<string[]>([]);
  const queryClient = useQueryClient();
  const [isAddDishModalOpen, setIsAddDishModalOpen] = useState(false);

  const notes = {
    note1: t("food.Heated Further"),
    note2: t("food.Faulty"),
    note3: t("food.Procedure"),
    note4: t("food.Food Heating Other"),
  };

  const heatFoodSchema = useMemo(
    () =>
      z
        .object({
          dishName: z.string().min(1, t("food.Product Name is Required")),
          startTemperature: z
            .string()
            .min(1, t("food.Temperature is Required")),
          remark: z
            .string()
            .optional()
            .refine(
              (data) => {
                return !isRemarkRequired || data;
              },
              {
                message: t("food.Remarks are mandatory"),
              },
            ),
        })
        .refine(
          (data) => {
            if (selectedHeat?.color !== AcceptenceCriteriaColors.Acceptable) {
              const isMandatoryFieldSelected = correctiveActions?.includes(
                notes.note4,
              );
              const remarksFilled = data.remark && data.remark?.length > 0;
              if (isMandatoryFieldSelected && remarksFilled) {
                return true;
              }
              if (!isMandatoryFieldSelected && correctiveActions?.length > 0) {
                return true;
              }
              return false;
            }
            return true;
          },
          {
            message: t("food.Corrective Action Remark Required"),
            path: ["remark"],
          },
        ),
    [isRemarkRequired, notes.note4, correctiveActions, selectedHeat?.color, t],
  );

  type HeatFoodSchema = z.infer<typeof heatFoodSchema>;

  const {
    formState: state,
    reset,
    register,
    handleSubmit,
    setValue,
  } = useForm<HeatFoodSchema>({
    resolver: zodResolver(heatFoodSchema),
  });

  const valid = useMemo(
    () =>
      selectedHeat?.color === AcceptenceCriteriaColors.Acceptable ||
      selectedHeat?.color === undefined,
    [selectedHeat],
  );

  const onCheck = (value: string) => {
    if (correctiveActions.includes(value)) {
      setCorrectiveActions(correctiveActions.filter((r) => r !== value));
    } else {
      setCorrectiveActions([...correctiveActions, value]);
    }
    if (value === notes.note4) {
      setIsRemarkRequired(!isRemarkRequired);
      if (isRemarkRequired) {
        reset({ remark: "" });
      }
    }
  };

  const handleOnClose = () => {
    setSelectedHeat(undefined);
    setCorrectiveActions([]);
    reset();
    setIsRemarkRequired(false);
    props.onClose?.();
  };

  const updateMutate = useMutation({
    mutationFn: updateFoodProductionControl,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      handleOnClose();
      toast.success(t("food.Heat Control Added Successfully"));
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("food.Failed to add Heat Control"));
    },
  });

  const mutation = useMutation({
    mutationFn: addFoodProductionControl,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance.companyLocationId,
        ],
      });
      handleOnClose();
      toast.success(t("food.Heat Control Added Successfully"));
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("food.Failed to add Heat Control"));
    },
  });

  const onSubmit = useCallback(
    (data: HeatFoodSchema) => {
      const isCompeletedWithErrors = !!correctiveActions.length;

      const payload = {
        companyId: foodControlPointInstance.companyId,
        companyLocationId: foodControlPointInstance.companyLocationId,
        reason: JSON.stringify(correctiveActions),
        remark: data.remark,

        status: isCompeletedWithErrors
          ? InstanceStatus.completedWithErrors
          : InstanceStatus.completed,
        startTemperature: Number(selectedHeat?.value),
        finalTemperature: Number(selectedHeat?.value),
        dishName: data.dishName,
        controlPointInstanceId: foodControlPointInstance.id,
        foodControlPointInstance,
      };

      if (initialData) {
        updateMutate.mutate(payload);
        return;
      }
      mutation.mutate(payload);
    },
    [
      correctiveActions,
      foodControlPointInstance,
      selectedHeat?.value,
      initialData,
      mutation,
      updateMutate,
    ],
  );

  useEffect(() => {
    if (initialData) {
      const { remark, reason } = parseRemarkAndCorrectiveActions(
        initialData.note,
      );

      setSelectedHeat(
        heatTreatmentTemperatures.find(
          (h) => h.value === `${initialData.startTemperature}`,
        ),
      );
      reset({
        remark,
        dishName: initialData.dishName,
        startTemperature: `${initialData.startTemperature}`,
      });

      setCorrectiveActions(reason);
    }
  }, [initialData, reset, setValue]);

  return (
    <>
      <Modal
        {...props}
        onClose={handleOnClose}
        actionButtons={
          <>
            <Button
              variant="secondary"
              onClick={props.onClose}
              disabled={mutation.isPending}
            >
              {t("common.Discard")}
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              loading={mutation.isPending}
            >
              {t("food.Submit Entry")}
            </Button>
          </>
        }
        title={
          type === FoodManagementControls.heatingTreatment
            ? t("food.Heating Treatment")
            : t("food.Reheating Food")
        }
      >
        <form>
          <div className="flex flex-col gap-2.5">
            <Label title={t("food.Name of the Product")} required>
              <DishSelection
                companyId={foodControlPointInstance.companyId}
                onAddNew={() => setIsAddDishModalOpen(true)}
                onChange={(e) => setValue("dishName", e.target.name)}
                defaultValue={initialData?.dishName}
              />
            </Label>
            <ErrorMessage message={state.errors.dishName?.message} />
          </div>
          <div className="flex flex-col gap-2.5">
            <Label title={t("food.Heating Temperature")} required>
              <Select
                placeholder={t("food.Select Temperature")}
                {...register("startTemperature")}
                key={foodControlPointInstance.id}
                options={heatTreatmentTemperatures}
                defaultValue={`${initialData?.startTemperature}`}
                onChange={(e) => {
                  const temp = heatTreatmentTemperatures.find(
                    (h) => h.value === e.target.value,
                  );
                  setSelectedHeat(temp);

                  if (temp?.color === AcceptenceCriteriaColors.Acceptable) {
                    setCorrectiveActions([]);
                    setIsRemarkRequired(false);
                  }
                }}
              />
            </Label>
            <ErrorMessage message={state.errors.startTemperature?.message} />
          </div>

          {!valid && (
            <Label
              title={t("food.Corrective Action")}
              labelAlignment="start"
              required
            >
              <div className="flex flex-col">
                <Label contentAlignment="right" title={notes.note1}>
                  <Input
                    size="sm"
                    checkbox
                    type="checkBox"
                    value={notes.note1}
                    defaultChecked={correctiveActions.includes(notes.note1)}
                    onChange={(e) => onCheck(e.target.value)}
                  />
                </Label>
                <Label
                  contentAlignment="right"
                  labelAlignment="start"
                  title={notes.note2}
                >
                  <Input
                    size="sm"
                    checkbox
                    type="checkBox"
                    defaultChecked={correctiveActions.includes(notes.note2)}
                    value={notes.note2}
                    onChange={(e) => onCheck(e.target.value)}
                  />
                </Label>
                <Label contentAlignment="right" title={notes.note3}>
                  <Input
                    size="sm"
                    checkbox
                    type="checkBox"
                    defaultChecked={correctiveActions.includes(notes.note3)}
                    value={notes.note3}
                    onChange={(e) => onCheck(e.target.value)}
                  />
                </Label>
                <Label contentAlignment="right" title={notes.note4}>
                  <Input
                    size="sm"
                    checkbox
                    defaultChecked={correctiveActions.includes(notes.note4)}
                    type="checkBox"
                    value={notes.note4}
                    onChange={(e) => onCheck(e.target.value)}
                  />
                </Label>
              </div>
            </Label>
          )}
          <div className="flex flex-col gap-2.5">
            <Label title={t("food.Remark")} required={isRemarkRequired}>
              <Input {...register("remark")} showRequired={isRemarkRequired} />
            </Label>
            <ErrorMessage message={state.errors.remark?.message} />
          </div>
        </form>
      </Modal>
      <UpsertProcessingProductModal
        open={isAddDishModalOpen}
        onClose={() => setIsAddDishModalOpen(false)}
        companyId={foodControlPointInstance.companyId}
        selectedLocationId={foodControlPointInstance.companyLocationId}
        type={UpsertModalType.Add}
      />
    </>
  );
}
